/** Split-pane talking-head crop: leftover rect + source fill so the face occupies the pane. */

import {
  BROLL_SPLIT_HALF,
  BROLL_SPLIT_HALF_HORIZONTAL,
  BROLL_TRIPLE_HORIZONTAL,
  isLayoutBrollType,
} from '@/lib/reelsShortsBroll';
import { isLayoutBrollClip } from '@/lib/videoEditorHostOverlays';

const FACE_FOCUS_Y = 0.55;
const SUBJECT_DELTA = 28;
const MIN_SUBJECT_RATIO = 0.012;
const MAX_SUBJECT_RATIO = 0.92;

export function layerToNormBox(layer) {
  if (!layer || typeof layer !== 'object') return null;
  const width = Number(layer.width);
  const height = Number(layer.height);
  const x = Number(layer.x);
  const y = Number(layer.y);
  if (![width, height, x, y].every((n) => Number.isFinite(n))) return null;
  return {
    x: x - width / 2,
    y: y - height / 2,
    w: width,
    h: height,
  };
}

export function talkingHeadRectBesideBroll(box, kind, position = '') {
  const type = String(kind || '');
  const pos = String(position || '').replace(/-/g, '_').toLowerCase();
  if (type === BROLL_SPLIT_HALF_HORIZONTAL) {
    const side = pos === 'top' || pos === 'bottom'
      ? pos
      : ((box.y + box.h / 2) < 0.5 ? 'bottom' : 'top');
    if (side === 'top') {
      return { x: 0, y: 0, w: 1, h: Math.max(box.y, 0.5) };
    }
    const top = box.y + box.h;
    return { x: 0, y: top, w: 1, h: Math.max(1 - top, 0.5) };
  }
  const side = pos === 'left' || pos === 'right'
    ? pos
    : ((box.x + box.w / 2) < 0.5 ? 'right' : 'left');
  if (side === 'left') {
    return { x: 0, y: 0, w: Math.max(box.x, 0.5), h: 1 };
  }
  const left = box.x + box.w;
  return { x: left, y: 0, w: Math.max(1 - left, 0.5), h: 1 };
}

export function talkingHeadPaneFromLayoutClip(clip) {
  if (!clip || !isLayoutBrollClip(clip)) return null;
  const type = String(clip.brollType || clip.broll_type || '');
  if (!isLayoutBrollType(type)) return null;
  if (type === BROLL_TRIPLE_HORIZONTAL) {
    const band = 420 / 1920;
    return { x: 0, y: band, w: 1, h: 1 - 2 * band };
  }
  const box = layerToNormBox(clip.layer);
  if (!box) {
    if (type === BROLL_SPLIT_HALF_HORIZONTAL) {
      const bottom = String(clip.position || '').toLowerCase() === 'bottom';
      return bottom ? { x: 0, y: 0.5, w: 1, h: 0.5 } : { x: 0, y: 0, w: 1, h: 0.5 };
    }
    if (type === BROLL_SPLIT_HALF) {
      const right = String(clip.position || '').toLowerCase() === 'right';
      return right ? { x: 0.5, y: 0, w: 0.5, h: 1 } : { x: 0, y: 0, w: 0.5, h: 1 };
    }
    return null;
  }
  return talkingHeadRectBesideBroll(box, type, clip.position);
}

export function sourceFillCrop(
  srcW,
  srcH,
  dstW,
  dstH,
  box,
  {
    padX = 0.18,
    padTop = 0.3,
    padBottom = 0.48,
  } = {},
) {
  const sw = Math.max(2, Number(srcW) || 0);
  const sh = Math.max(2, Number(srcH) || 0);
  const dw = Math.max(2, Number(dstW) || 0);
  const dh = Math.max(2, Number(dstH) || 0);
  const destAspect = dw / dh;
  const x = Number(box?.x) || 0;
  const y = Number(box?.y) || 0;
  const bw = Math.max(1, Number(box?.w) || 0);
  const bh = Math.max(1, Number(box?.h) || 0);
  const left = x - bw * padX;
  const top = y - bh * padTop;
  const right = x + bw + bw * padX;
  const bottom = y + bh + bh * padBottom;
  let boxW = Math.max(8, right - left);
  let boxH = Math.max(8, bottom - top);
  let cw;
  let ch;
  if (boxW / boxH > destAspect) {
    cw = boxW;
    ch = boxW / destAspect;
  } else {
    ch = boxH;
    cw = boxH * destAspect;
  }
  if (cw > sw) {
    cw = sw;
    ch = cw / destAspect;
  }
  if (ch > sh) {
    ch = sh;
    cw = ch * destAspect;
  }
  if (cw > sw) {
    cw = sw;
    ch = Math.min(sh, cw / destAspect);
  }
  cw = Math.min(Math.max(2, cw), sw);
  ch = Math.min(Math.max(2, ch), sh);
  const faceCx = x + bw / 2;
  const faceCy = y + bh * FACE_FOCUS_Y;
  const cx = Math.min(Math.max(0, faceCx - cw / 2), Math.max(0, sw - cw));
  const cy = Math.min(Math.max(0, faceCy - ch / 2), Math.max(0, sh - ch));
  return {
    x: Math.round(cx),
    y: Math.round(cy),
    w: Math.round(cw),
    h: Math.round(ch),
  };
}

export function videoSourceCropStyle(srcW, srcH, crop, paneW, paneH) {
  const sw = Number(srcW) || 0;
  const sh = Number(srcH) || 0;
  const pw = Number(paneW) || 0;
  const ph = Number(paneH) || 0;
  const cw = Number(crop?.w) || 0;
  const ch = Number(crop?.h) || 0;
  if (sw < 8 || sh < 8 || pw < 2 || ph < 2 || cw < 2 || ch < 2) return null;
  const scale = Math.max(pw / cw, ph / ch);
  return {
    position: 'absolute',
    left: `${-Number(crop.x) * scale}px`,
    top: `${-Number(crop.y) * scale}px`,
    width: `${sw * scale}px`,
    height: `${sh * scale}px`,
    maxWidth: 'none',
    objectFit: 'fill',
  };
}

export function refineSubjectBox(x0, y0, x1, y1, frameW, frameH) {
  const left = Math.max(0, Math.round(x0));
  const top = Math.max(0, Math.round(y0));
  const right = Math.min(frameW - 1, Math.round(x1));
  const bottom = Math.min(frameH - 1, Math.round(y1));
  const bw = Math.max(1, right - left);
  const bh = Math.max(1, bottom - top);
  const squatHair = top < frameH * 0.5 && bh < bw * 1.4 && bottom < frameH * 0.62;
  if (squatHair) {
    const grown = Math.min(frameH - 1, top + Math.max(bh, Math.round(bw * 1.7)));
    const pad = Math.max(2, Math.round(bw * 0.08));
    const nx0 = Math.max(0, left - pad);
    const nx1 = Math.min(frameW - 1, right + pad);
    return { x: nx0, y: top, w: nx1 - nx0, h: grown - top };
  }
  if (bh > bw * 1.55) {
    return { x: left, y: top, w: bw, h: Math.min(bh, Math.max(Math.round(bw * 1.4), Math.round(bh * 0.7))) };
  }
  return { x: left, y: top, w: bw, h: bh };
}

export function subjectBoxFromImageData(imageData) {
  if (!imageData?.data || !imageData.width || !imageData.height) return null;
  const { data, width, height } = imageData;
  const ring = [];
  const push = (i) => ring.push([data[i], data[i + 1], data[i + 2]]);
  for (let x = 0; x < width; x += 1) {
    push((x * 4));
    push(((height - 1) * width + x) * 4);
  }
  for (let y = 0; y < height; y += 1) {
    push((y * width) * 4);
    push((y * width + width - 1) * 4);
  }
  const mid = (channel) => {
    const vals = ring.map((p) => p[channel]).sort((a, b) => a - b);
    return vals[Math.floor(vals.length / 2)] || 0;
  };
  const backdrop = [mid(0), mid(1), mid(2)];
  let minX = width;
  let minY = height;
  let maxX = 0;
  let maxY = 0;
  let count = 0;
  const total = width * height;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4;
      const d = Math.max(
        Math.abs(data[i] - backdrop[0]),
        Math.abs(data[i + 1] - backdrop[1]),
        Math.abs(data[i + 2] - backdrop[2]),
      );
      if (d < SUBJECT_DELTA) continue;
      count += 1;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  const ratio = count / total;
  if (ratio < MIN_SUBJECT_RATIO || ratio > MAX_SUBJECT_RATIO) return null;
  return refineSubjectBox(minX, minY, maxX, maxY, width, height);
}

export function grabFrameFromVideoElement(video) {
  if (!video || !(video.videoWidth > 0) || !(video.videoHeight > 0)) return null;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0);
    return {
      dataUrl: canvas.toDataURL('image/jpeg', 0.82),
      srcW: canvas.width,
      srcH: canvas.height,
      canvas,
    };
  } catch {
    return null;
  }
}

export function localFillCropFromCanvas(canvas, dstW, dstH) {
  if (!canvas) return null;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const box = subjectBoxFromImageData(ctx.getImageData(0, 0, canvas.width, canvas.height));
  if (!box) return null;
  return {
    srcW: canvas.width,
    srcH: canvas.height,
    crop: sourceFillCrop(canvas.width, canvas.height, dstW, dstH, box),
    method: 'subject',
  };
}

export function talkPaneScreenRect(contentRect, pane) {
  if (!contentRect || !pane) return null;
  const width = Number(contentRect.width) || 0;
  const height = Number(contentRect.height) || 0;
  if (width < 4 || height < 4) return null;
  return {
    left: (Number(contentRect.left) || 0) + pane.x * width,
    top: (Number(contentRect.top) || 0) + pane.y * height,
    width: pane.w * width,
    height: pane.h * height,
  };
}
